import { useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.7, ease: "easeOut" } }),
};

const socialLinks = [
  { label: "Instagram", href: "https://www.instagram.com/phoonmusic" },
  { label: "SoundCloud", href: "https://soundcloud.com/musicbyphoon" },
  { label: "Spotify", href: "https://open.spotify.com/artist/4DKokEWRuxYx2XItcPgUQe?si=5wsBb6SoRNO6o52UbizzNg" },
  { label: "YouTube", href: "https://www.youtube.com/@phoonmusic" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", venue: "", message: "" });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        venue: form.venue || null,
        event_date: date ? format(date, "yyyy-MM-dd") : null,
        message: form.message,
      });
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main data-testid="contact-page" className="pt-20 min-h-screen flex items-center justify-center">
        <motion.div
          className="text-center px-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 data-testid="success-heading" className="font-['Black_Han_Sans'] text-4xl md:text-6xl text-[#F4EFE6] mb-4 uppercase">
            Message Sent
          </h2>
          <p className="text-[#F4EFE6]/70 text-lg">We'll be in touch soon.</p>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="contact-page" className="pt-20">
      <SEO title="Contact" description="Book Phöön for your event. Booking inquiries, press, and general contact." path="/contact" />
      {/* Page Header */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h1
            data-testid="contact-page-title"
            className="font-['Black_Han_Sans'] text-5xl md:text-7xl text-[#F4EFE6] uppercase"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Let's Work
          </motion.h1>
          <motion.p
            className="mt-4 text-lg text-[#F4EFE6]/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            For booking inquiries, press, and general contact
          </motion.p>
        </div>
      </section>

      {/* Two Column Layout */}
      <section className="pb-24 md:pb-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-16 md:gap-20">
            {/* Left Column — Info */}
            <motion.div
              className="md:col-span-2 flex flex-col justify-start"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
            >
              <div className="space-y-8">
                <div>
                  <p className="font-mono-label text-sm text-[#FF5500] mb-3">Booking</p>
                  <a
                    href="mailto:phoonforbookings@gmail.com"
                    data-testid="contact-booking-email"
                    className="text-[#F4EFE6] hover:text-[#FF5500] transition-colors duration-300 text-lg font-bold"
                  >
                    phoonforbookings@gmail.com
                  </a>
                </div>

                <div>
                  <p className="font-mono-label text-sm text-[#FF5500] mb-3">Based In</p>
                  <p className="text-[#F4EFE6]/60">Chicago, available worldwide</p>
                </div>

                <div>
                  <p className="font-mono-label text-sm text-[#FF5500] mb-4">Follow</p>
                  <div className="flex flex-col gap-2">
                    {socialLinks.map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`contact-social-${s.label.toLowerCase()}`}
                        className="font-mono-label text-xs text-[#F4EFE6]/60 hover:text-[#FF5500] transition-colors duration-300"
                      >
                        {s.label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column — Form */}
            <motion.div
              className="md:col-span-3"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} custom={1}
            >
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                <div>
                  <label className="font-mono-label text-sm text-[#FF5500] mb-2 block">
                    Name <span className="text-[#FF5500]">*</span>
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] transition-colors h-12 text-base"
                  />
                </div>

                <div>
                  <label className="font-mono-label text-sm text-[#FF5500] mb-2 block">
                    Email <span className="text-[#FF5500]">*</span>
                  </label>
                  <Input
                    data-testid="contact-email-input"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                    className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] transition-colors h-12 text-base"
                  />
                </div>

                <div>
                  <label className="font-mono-label text-sm text-[#FF5500] mb-2 block">
                    Event / Venue Name
                  </label>
                  <Input
                    data-testid="contact-venue-input"
                    name="venue"
                    value={form.venue}
                    onChange={handleChange}
                    placeholder="Venue or event name"
                    className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] transition-colors h-12 text-base"
                  />
                </div>

                <div>
                  <label className="font-mono-label text-sm text-[#FF5500] mb-2 block">
                    Event Date
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        data-testid="contact-date-picker"
                        type="button"
                        className="flex items-center justify-between w-full bg-transparent border-0 border-b border-[#FF5500]/25 px-0 h-12 text-left hover:border-[#FF5500] transition-colors"
                      >
                        <span className={date ? "text-[#F4EFE6]" : "text-[#F4EFE6]/20"}>
                          {date ? format(date, "MMMM d, yyyy") : "Select a date"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-[#FF5500]/50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0c1128] border-[#FF5500]/25" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        data-testid="contact-calendar"
                        className="bg-[#0c1128] text-[#F4EFE6]"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="font-mono-label text-sm text-[#FF5500] mb-2 block">
                    Message <span className="text-[#FF5500]">*</span>
                  </label>
                  <Textarea
                    data-testid="contact-message-input"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your event..."
                    rows={5}
                    className="bg-transparent border-0 border-b border-[#FF5500]/25 rounded-none px-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/20 focus-visible:ring-0 focus-visible:border-[#FF5500] transition-colors resize-none text-base"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="flex items-center gap-3 bg-[#FF5500] text-[#F4EFE6] font-['Black_Han_Sans'] text-xl uppercase tracking-wider px-10 py-3 hover:bg-[#FF5500]/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                >
                  {loading ? "SENDING..." : "SEND MESSAGE"}
                  <Send size={18} />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Cards */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-mono-label text-base text-[#FF5500] mb-8">Connect</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: "Instagram", handle: "@phoonmusic", href: "https://www.instagram.com/phoonmusic", icon: (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10 md:w-14 md:h-14">
                  <rect x="2" y="2" width="20" height="20" rx="5" />
                  <circle cx="12" cy="12" r="5" />
                  <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
                </svg>
              )},
              { name: "SoundCloud", handle: "@musicbyphoon", href: "https://soundcloud.com/musicbyphoon", icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 md:w-14 md:h-14">
                  <path d="M1 18V14h1v4H1zm2.5-5v5h1v-5h-1zM6 11v7h1v-7H6zm2.5-1v8h1v-8h-1zM11 8v10h1V8h-1zm2.5 1v9h1v-9h-1zM16 7v11h1V7h-1zm2.5 2v9h1V9h-1zM21 10v8h1v-8h-1z" />
                </svg>
              )},
              { name: "Spotify", handle: "Phöön", href: "https://open.spotify.com/artist/4DKokEWRuxYx2XItcPgUQe?si=5wsBb6SoRNO6o52UbizzNg", icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 md:w-14 md:h-14">
                  <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424a.623.623 0 01-.857.207c-2.348-1.435-5.304-1.76-8.785-.964a.623.623 0 11-.277-1.215c3.809-.87 7.076-.496 9.712 1.115a.623.623 0 01.207.857zm1.225-2.718a.78.78 0 01-1.072.257c-2.687-1.652-6.785-2.131-9.965-1.166a.78.78 0 01-.452-1.492c3.632-1.102 8.147-.568 11.234 1.329a.78.78 0 01.255 1.072zm.105-2.835C14.692 8.95 9.375 8.775 6.297 9.71a.935.935 0 11-.543-1.79c3.532-1.072 9.404-.865 13.115 1.338a.935.935 0 01-1.053 1.613z" />
                </svg>
              )},
              { name: "YouTube", handle: "Phöön Live @ Halcyon SF", href: "https://www.youtube.com/@phoonmusic", icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 md:w-14 md:h-14">
                  <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              )},
            ].map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`contact-social-card-${social.name.toLowerCase()}`}
                className="bg-[#0c1128] border border-[#FF5500]/10 p-6 md:p-8 flex flex-col items-center text-center hover:border-[#FF5500]/40 transition-all duration-300 group"
              >
                <div className="text-[#F4EFE6] group-hover:text-[#FF5500] transition-colors duration-300 mb-4 md:mb-6">
                  {social.icon}
                </div>
                <h3 className="font-['Black_Han_Sans'] text-lg md:text-xl text-[#F4EFE6] uppercase">
                  {social.name}
                </h3>
                <p className="font-mono-label text-[9px] md:text-[10px] text-[#FF5500]/60 mt-2">
                  {social.handle}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Direct Contact Line */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="font-mono-label text-xs text-[#F4EFE6]/50">
            Direct booking:{" "}
            <a
              href="mailto:phoonforbookings@gmail.com"
              data-testid="contact-direct-email"
              className="text-[#F4EFE6]/70 hover:text-[#FF5500] transition-colors duration-300"
            >
              phoonforbookings@gmail.com
            </a>
          </p>
        </div>
      </section>
    </main>
  );
}
